import { Button, Classes, FormGroup, Icon, Intent, MenuItem } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import { ItemRenderer, Select } from '@blueprintjs/select'
import classNames from 'classnames'
import React, { Component, MouseEvent, SyntheticEvent } from 'react'
import { connect } from 'react-redux'

import { LoadingIndicator } from 'modules/forms'
import { DispatchProp } from 'modules/store'

import * as fromStore from '../../../app/admin/store'
import { i18nKeys } from '../../../app/app.i18n'
import { isEmpty, isPresent } from '../../../app/shared'

import { SyncIndicator } from '../sync-indicator'
import { FieldAdapterProps, SuggestProps } from '../types'
import { createHelperText, createLabelWithId, createRequiredLabel, getIntent, identity } from '../utils'
import * as fromSuggest from './suggest-adapter.actions'
import { getSuggestData, getSuggestLoading, selectSuggestState } from './suggest-adapter.reducer'
import { SuggestActionPayload, SuggestOption } from './types'

import './suggest-adapter.scss'

export type StateProps = {
  options: SuggestOption[]
  isLoading: boolean
}

type OwnProps = SuggestActionPayload & {
  initialValue?: SuggestOption
  normalize(data: any[]): SuggestOption[]
}

export type Props = FieldAdapterProps<SuggestProps> & StateProps & DispatchProp & OwnProps

const mapStateToProps = (state: fromStore.AppState, { storeId, normalize }: OwnProps) => ({
  options: getSuggestData<SuggestOption[]>(selectSuggestState(state), storeId, normalize),
  isLoading: getSuggestLoading(selectSuggestState(state), storeId),
})

const enhance = connect(mapStateToProps)

const noSelectionId = undefined
const noSelectionItem: SuggestOption = {
  id: noSelectionId,
  name: i18nKeys.core.common.selectEmptyOptionText,
  label: '',
}

const SelectLookup = Select.ofType<SuggestOption>()

class SuggestAdapter extends Component<Props> {
  componentDidUpdate(prevProps: Props) {
    const {
      input: { value: prevValue },
    } = prevProps
    const {
      input: { value: actualValue },
      dispatch,
      storeId,
    } = this.props

    if (prevValue !== actualValue && actualValue === noSelectionId) {
      dispatch(new fromSuggest.CleanSuggestDataAction(storeId))
    }
  }

  componentWillUnmount() {
    this.props.dispatch(new fromSuggest.CleanSuggestDataAction(this.props.storeId))
  }

  render() {
    const {
      input,
      meta,
      helperText,
      disabled = false,
      inline,
      required,
      intent,
      label,
      options,
      translate = identity,
      enableSyncControl,
      // tslint:disable-next-line:no-empty
      onSyncChange = (fieldName: string, newValue: boolean) => {},
      isLoading,
    } = this.props

    const { id, labelText } = createLabelWithId(input, label)
    const intentType = getIntent(meta, intent)
    const helperTextMessage = createHelperText(meta, helperText, translate)
    const labelInfo = createRequiredLabel(required, translate)
    const formGroupClassName = required ? 'required' : ''

    const reqLabelMod = (
      <>
        {labelInfo}
        {enableSyncControl && (
          <SyncIndicator translate={translate} fieldName={input.name} disabled={disabled} onSyncChange={onSyncChange} />
        )}
      </>
    )

    const { onChange, value, ...inputRest } = input

    return (
      <FormGroup
        className={formGroupClassName}
        helperText={helperTextMessage}
        label={labelText}
        labelFor={id}
        labelInfo={reqLabelMod}
        intent={intentType}
        inline={inline}
        disabled={disabled}
      >
        <div className="suggest__input-group">
          <SelectLookup
            {...inputRest}
            disabled={disabled}
            popoverProps={{
              minimal: true,
              className: 'suggest__popover',
            }}
            resetOnClose
            itemRenderer={itemRenderer}
            items={options}
            onItemSelect={this.handleItemSelect}
            onQueryChange={this.handleTextChange}
            noResults={
              !isLoading ? (
                <MenuItem disabled text={translate(i18nKeys.core.common.suggest.noResults)} />
              ) : (
                <li>
                  <LoadingIndicator />
                </li>
              )
            }
          >
            <Button
              text={this.getSelectedValueText()}
              name={input.name}
              disabled={disabled}
              rightIcon={<Icon icon={IconNames.DOUBLE_CARET_VERTICAL} title={false} />}
            />
          </SelectLookup>
          <Button
            rightIcon={<Icon icon={IconNames.ERASER} title={false} />}
            onClick={this.handleReset}
            disabled={disabled || this.noSelectedValue()}
            minimal
            intent={Intent.PRIMARY}
          />
        </div>
      </FormGroup>
    )
  }

  private noSelectedValue = () => {
    return noSelectionId === this.props.input.value
  }

  private handleReset = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    event.preventDefault()
    this.props.input.onChange(noSelectionId)
  }

  private handleTextChange = (query: string, event?: SyntheticEvent<HTMLInputElement>) => {
    const { dispatch, storeId, params, endpointUrl } = this.props

    if (isEmpty(query)) {
      dispatch(new fromSuggest.CleanSuggestDataAction(storeId))
      this.props.input.onChange(noSelectionId)

      return
    }

    const newParams = {
      Search: query,
      ...params,
    }

    dispatch(
      new fromSuggest.GetSuggestDataAction({
        endpointUrl,
        params: newParams,

        storeId,
      })
    )
  }

  private handleItemSelect = (item?: SuggestOption) => {
    const value = isPresent(item) ? item.id : ''
    this.props.input.onChange(value)
  }

  private getSelectedValueText = (): string => {
    const {
      options,
      input: { value },
      initialValue,
      translate = identity,
    } = this.props

    if (isPresent(options) && !isEmpty(options)) {
      const found = options.find((option: SuggestOption) => option.id === value)

      return found ? found.name : translate(noSelectionItem.name)
    }

    if (value === noSelectionId) {
      return translate(noSelectionItem.name)
    }

    if (isPresent(initialValue) && isPresent(initialValue.id)) {
      return initialValue.name
    }

    return translate(noSelectionItem.name)
  }
}

export default enhance(SuggestAdapter)

const itemRenderer: ItemRenderer<SuggestOption> = (item, { handleClick, modifiers }) => {
  const classes = classNames({
    [Classes.ACTIVE]: modifiers.active,
    [Classes.INTENT_PRIMARY]: modifiers.active,
  })

  return (
    <MenuItem className={classes} label={item.label} key={`m-${item.id}`} onClick={handleClick} text={`${item.name}`} />
  )
}



// WEBPACK FOOTER //
// ./src/modules/forms/suggest-adapter/suggest-adapter.tsx